// ABOUTME: Favorite star of a model-selection family card, bookmarking the category node.
// ABOUTME: Reads the state marked on the catalog payload and reports toggles to the catalog favorites.

import type { ICategory } from '@coolshop/cool-model-selection';
import { Box, IconButton, StarBorderIcon, StarIcon } from '@ds/coolshop';
import {
    useCanAddFavorite,
    useFavoritesEnabled,
    useFavoriteToggle,
} from '@src/_new/modules/user-favorites';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { MouseEvent, useContext } from 'react';
import { useIntl } from 'react-intl';

import { resolveCategoryNodeId } from '../../utils/resolveProductFavoriteTarget';
import { categoryFavoriteKey, useCatalogFavorites } from './CatalogFavoritesContext';

// `isFavorite` is resolved server-side and carried on the catalog payload; the package type does
// not know about it.
type CategoryWithFavorite = ICategory & { isFavorite?: boolean };

/**
 * Bookmarks a family or series, a node of the model-selection hierarchy. Renders nothing when
 * favorites are disabled, the tenant has no model-selection hierarchy, or the user's roles may not
 * add a favorite on it (`favoriteRoles`) and the family is not one already.
 */
export function CategoryFavoriteAction({ category }: { category: ICategory }) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const favoritesEnabled = useFavoritesEnabled();
    const canAddFavorite = useCanAddFavorite();
    const catalogFavorites = useCatalogFavorites();
    const hierarchyId = settings.prototype?.hierarchies?.modelSelection ?? '';
    const { isFavorite: markedFavorite } = category as CategoryWithFavorite;
    // The favorite is the node's, addressed by its id: the code (`internalId`) changes on a rename.
    const nodeId = resolveCategoryNodeId(category);

    const { isFavorite, toggling, toggleWithFeedback } = useFavoriteToggle({
        hierarchyId,
        nodeId: nodeId ?? '',
        skip: !favoritesEnabled || !hierarchyId || !nodeId,
        knownIsFavorite: markedFavorite === true,
    });

    if (!favoritesEnabled || !hierarchyId || !nodeId) {
        return null;
    }
    // Removing is always allowed: a favorite already set keeps its star.
    if (!isFavorite && !canAddFavorite(hierarchyId)) {
        return null;
    }

    // The card itself is clickable (it opens the family), so the star's clicks must not reach it.
    const stop = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
    };

    return (
        <Box onClick={stop}>
            <IconButton
                size="small"
                aria-label={
                    isFavorite
                        ? formatMessage({
                              id: 'catalog.favorite.remove',
                              defaultMessage: 'Remove from favorites',
                          })
                        : formatMessage({
                              id: 'catalog.favorite.add',
                              defaultMessage: 'Add to favorites',
                          })
                }
                data-testid={`category-favorite-button-${nodeId}`}
                disabled={toggling}
                onClick={event => {
                    stop(event);
                    toggleWithFeedback().then(result => {
                        if (result) {
                            catalogFavorites?.setFavorite(
                                categoryFavoriteKey(nodeId),
                                result.isFavorite,
                            );
                        }
                    });
                }}
            >
                {isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
            </IconButton>
        </Box>
    );
}
